/**
 * Taking a live AI call over in this browser (ring R6d): claim it, open the
 * microphone, and walk this browser's leg into the takeover room.
 *
 * The AI is already talking to the callee. The press claims the call on the
 * server, and this browser connects to the voice webhook with
 * `params: { aiTakeover }` - never `callLogId`, which would send the leg down the
 * ordinary dial path and ring the callee a second time. The leg joins the room
 * FIRST; only then does the AI say "I am connecting you to a colleague" and
 * step out, and the callee is brought in. Until the server reports that bridge,
 * the window stays on "connecting you", exactly as a conference call waits for
 * its customer (`useCallEvents.ts`, `accept`).
 *
 * EVERY WAY OUT BEFORE THE BRIDGE CALLS THE ATTEMPT OFF, and none of them hangs
 * up on anybody: the refused microphone, a failed `connect`, the leg dropping,
 * and the operator pressing the red button all post `takeover/cancel`, and the
 * AI simply keeps talking. Without that the attempt would hold the call for its
 * whole deadline, and a second try would be refused as already running. After
 * the bridge a cancel is a no-op on the server, so the leg ending sends one
 * without having to know which side of the bridge it is on.
 *
 * Lives beside the provider rather than in it, like `useCallEvents.ts`: the
 * provider decides whether a call may start, and this is one more way to start
 * one. It is handed the provider's own refs, because the session guard, the
 * phase and the `Call` are the provider's, not this file's.
 */
import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import type { Call, Device } from "@twilio/voice-sdk";
import { cancelAiTakeover, startAiTakeover, type TakeoverCancelReason } from "../../../lib/aiTakeover";
import { applyAudioInput, applyAudioOutputs, type CallAudio } from "../audio/apply";
import { readAudioChoice } from "../audio/choice";
import { applyMicGain } from "../audio/gain";
import { readAudioVolume } from "../audio/volume";
import { startCallError } from "../callError";
import { IDLE, canStartCall, type CallPhase, type CallState } from "../callState";
import type { AttachCall } from "../useCallEvents";
import { takeoverCallState, takeoverStartError, type AiTakeoverTarget } from "./aiTakeoverStart";

export interface AiTakeoverDeps {
  enabled: boolean;
  phaseRef: MutableRefObject<CallPhase>;
  sessionRef: MutableRefObject<number>;
  conferenceRef: MutableRefObject<boolean>;
  callRef: MutableRefObject<Call | null>;
  setState: Dispatch<SetStateAction<CallState>>;
  setDialerOpen: (open: boolean) => void;
  acquire: () => Promise<Device>;
  release: () => void;
  attach: AttachCall;
}

export interface AiTakeoverControls {
  /** Take this AI call over in this browser. Does nothing while another call is up. */
  takeOverAiCall: (target: AiTakeoverTarget) => void;
  /**
   * The red button on a takeover still on its way in: close the window and call
   * the attempt off. The AI goes on talking to the callee.
   */
  callOffTakeover: (callLogId: string) => void;
}

/** Tell the server this browser's leg is not coming. Never throws: the deadline is the net. */
function callOff(callLogId: string, reason: TakeoverCancelReason): void {
  void cancelAiTakeover(callLogId, reason).catch(() => undefined);
}

export function useAiTakeover(deps: AiTakeoverDeps): AiTakeoverControls {
  const { enabled, phaseRef, sessionRef, conferenceRef, callRef, setState, setDialerOpen, acquire, release, attach } =
    deps;

  const takeOverAiCall = useCallback(
    (target: AiTakeoverTarget) => {
      if (!enabled || !target.callLogId || !canStartCall(phaseRef.current)) return;
      // Synchronously, the way `startCall` does it: a double click and an offer
      // arriving mid-request both read this before React renders again.
      phaseRef.current = "preparing";
      // The room's `accept` is the browser joining it alone. The window has to
      // wait for the bridge, which is what this ref tells the event handlers.
      conferenceRef.current = true;
      setDialerOpen(false);
      setState(takeoverCallState(target));

      const session = ++sessionRef.current;
      const live = () => sessionRef.current === session;
      const update = (patch: Partial<CallState>) => {
        if (live()) setState((prev) => ({ ...prev, ...patch }));
      };
      const id = target.callLogId;

      void (async () => {
        const started = await startAiTakeover(id, "browser").catch((err: unknown) => {
          // Nothing was claimed: nothing to call off.
          if (live()) update({ phase: "error", error: takeoverStartError(err) });
          return null;
        });
        if (!started) return;
        // This operator's attempt was already on its way - from another tab, or
        // on their mobile a second ago. A second leg would lose the latch, and
        // its cancel would call off the attempt that is working.
        if (started.already) {
          if (live()) update({ phase: "error", error: "takeover_elsewhere" });
          return;
        }
        // Claimed. The operator closed the window while the request was out.
        if (!live()) return callOff(id, "operator_left");

        try {
          const device = await acquire();
          if (!live()) {
            release();
            return callOff(id, "operator_left");
          }
          // The same routing and at the same moment as `startCall`: the chosen
          // speaker and microphone, the input level, right before `connect`.
          const chosen = readAudioChoice();
          const routing = device.audio as CallAudio | null;
          await applyAudioOutputs(routing, chosen);
          await applyMicGain(routing, readAudioVolume().input);
          await applyAudioInput(routing, chosen);
          if (!live()) {
            release();
            return callOff(id, "operator_left");
          }

          update({ phase: "connecting" });
          const call = await device.connect({ params: { aiTakeover: id } });
          if (!live()) {
            call.disconnect();
            return callOff(id, "operator_left");
          }
          attach(call, live, update);
          // After `attach`, so the window has already painted the ending. Once:
          // an error and its disconnect arrive together. Not after the operator's
          // own red button, which has already said why (see `callOffTakeover`).
          let calledOff = false;
          const legEnded = () => {
            if (calledOff || !live()) return;
            calledOff = true;
            callOff(id, "browser_failed");
          };
          call.on("disconnect", legEnded);
          call.on("error", legEnded);
        } catch (err) {
          if (!live()) return;
          // A refused microphone or a failed connect: the window says which, as
          // it would for a call of our own, and the AI keeps the call.
          update({ phase: "error", error: startCallError(err) });
          release();
          callOff(id, "browser_failed");
        }
      })();
    },
    [enabled, phaseRef, sessionRef, conferenceRef, setState, setDialerOpen, acquire, release, attach],
  );

  const callOffTakeover = useCallback(
    (callLogId: string) => {
      const call = callRef.current;
      // The session moves first, so the leg's own `disconnect` below finds
      // itself stale and does not call the attempt off a second time.
      sessionRef.current++;
      callRef.current = null;
      try {
        call?.disconnect();
      } catch {
        // Already gone.
      }
      release();
      phaseRef.current = "idle";
      setState(IDLE);
      callOff(callLogId, "operator_left");
    },
    [callRef, sessionRef, phaseRef, setState, release],
  );

  return { takeOverAiCall, callOffTakeover };
}
